import React, {useState, useMemo, useCallback} from 'react';
import {useSwipeable} from 'react-swipeable';
import {thinMargins, wideMargins} from '../../constants';
import {usePageWidth} from '../../utils';
import {useSetCarouselInterval} from '../../utils/setCarouselInterval';

export interface IPartnerTestimonial {
  quote: string;
  name: string;
  role: string;
}

const cardBgs = ['bg-background', 'bg-sandwisp'];

const LOGO_CARD_WIDTH = 192; // px (12rem)
const LOGO_CARD_STEP = 208; // card + gap (13rem)

const LogoCard = ({name, img}: {name: string; img: string}) => (
  <div
    style={{minWidth: LOGO_CARD_WIDTH, width: LOGO_CARD_WIDTH}}
    className="bg-white/30 rounded-xl flex items-center justify-center px-6 py-4 h-[5.5rem]">
    <img
      src={img}
      alt={name}
      className="max-h-[3rem] max-w-[8rem] object-contain"
    />
  </div>
);

const TestimonialCard = ({
  quote,
  name,
  role,
  cardBg,
}: IPartnerTestimonial & {cardBg: string}) => (
  <div
    className={`${cardBg} rounded-2xl p-8 flex flex-col gap-6 justify-between h-full`}>
    <div className="flex flex-col gap-4">
      <span className="text-hotcalypso text-subtitlesmall font-title leading-none">
        &ldquo;
      </span>
      <p className="text-largebody font-title">{quote}</p>
      <span className="text-hotcalypso text-subtitlesmall font-title leading-none text-right">
        &rdquo;
      </span>
    </div>
    <div>
      <p className="font-semibold text-smallbody">{name}</p>
      <p className="text-smallbodymob text-gray-500">{role}</p>
    </div>
  </div>
);

interface IPartnerSection {
  title: string;
  logos: {name: string; img: string}[];
  subtitle: string;
  testimonials: IPartnerTestimonial[];
}

export const PartnerSection = ({
  title,
  logos,
  subtitle,
  testimonials,
}: IPartnerSection) => {
  const pageWidth = usePageWidth();

  // Logo carousel (mobile)
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [isLogoPaused, setIsLogoPaused] = useState(false);
  const lastLogoIndex = logos.length;

  const onPrevLogo = useCallback(() => {
    setIsLogoPaused(true);
    setCurrentLogoIndex(v => (v === 0 ? lastLogoIndex - 1 : v - 1));
  }, [lastLogoIndex]);

  const onNextLogo = useCallback(() => {
    setIsLogoPaused(true);
    setCurrentLogoIndex(v => (v === lastLogoIndex - 1 ? 0 : v + 1));
  }, [lastLogoIndex]);

  const logoHandlers = useSwipeable({
    onSwipedLeft: onNextLogo,
    onSwipedRight: onPrevLogo,
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  useSetCarouselInterval({
    isSlideshowPaused: isLogoPaused,
    currentSlideIndex: currentLogoIndex,
    lastSlideIndex: lastLogoIndex,
    onSetCurrentSlideIndex: setCurrentLogoIndex,
    time: 2200,
  });

  // Testimonial carousel (mobile)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const lastSlideIndex = testimonials.length;

  const onGoToPreviousSlide = useCallback(() => {
    setCurrentSlideIndex(v => (v === 0 ? lastSlideIndex - 1 : v - 1));
  }, [lastSlideIndex]);

  const onGoToNextSlide = useCallback(() => {
    setCurrentSlideIndex(v => (v === lastSlideIndex - 1 ? 0 : v + 1));
  }, [lastSlideIndex]);

  const isLastSlide = useMemo(
    () => currentSlideIndex === lastSlideIndex,
    [currentSlideIndex, lastSlideIndex],
  );

  const testimonialHandlers = useSwipeable({
    onSwipedLeft: onGoToNextSlide,
    onSwipedRight: onGoToPreviousSlide,
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  const logoMarginLeft = pageWidth / 2 - LOGO_CARD_WIDTH / 2;

  return (
    <div className="py-12 lg:py-20 flex flex-col gap-8">
      <p
        className={`${thinMargins} text-h3mob lg:text-h3 font-bold text-center text-black`}>
        {title}
      </p>

      {/* Logo carousel — mobile only, full-bleed for peek effect */}
      <div className="lg:hidden overflow-x-hidden" {...logoHandlers}>
        <div
          className="flex gap-4 items-center transition ease-out duration-700"
          style={{
            marginLeft: logoMarginLeft,
            transform: `translateX(-${currentLogoIndex * LOGO_CARD_STEP}px)`,
          }}>
          {logos.map((logo, i) => (
            <LogoCard key={i} name={logo.name} img={logo.img} />
          ))}
        </div>
      </div>

      {/* Logo cards — desktop only */}
      <div
        className={`${thinMargins} hidden lg:flex gap-4 justify-center flex-wrap`}>
        {logos.map((logo, i) => (
          <LogoCard key={i} name={logo.name} img={logo.img} />
        ))}
      </div>
      <div className={`${wideMargins} text-black text-center`}>
        <p className="text-largebodymob lg:text-largebody ">{subtitle}</p>
      </div>
      {/* Testimonials — desktop */}
      {testimonials.length > 0 && (
        <div className={`${thinMargins} hidden lg:grid grid-cols-2 gap-6`}>
          {testimonials.map((t, i) => (
            <TestimonialCard
              key={i}
              {...t}
              cardBg={cardBgs[i % cardBgs.length]}
            />
          ))}
        </div>
      )}

      {/* Testimonials — mobile carousel */}
      {testimonials.length > 0 && (
        <div className="lg:hidden" {...testimonialHandlers}>
          <div className="overflow-hidden">
            <div
              className="flex transition ease-out duration-200"
              style={{
                width: pageWidth,
                transform: isLastSlide
                  ? ''
                  : `translateX(-${currentSlideIndex * 100}%)`,
              }}>
              {testimonials.map((t, i) => (
                <div key={i} className="min-w-[100%] px-[5%]">
                  <TestimonialCard
                    {...t}
                    cardBg={cardBgs[i % cardBgs.length]}
                  />
                </div>
              ))}
            </div>
            <div className="py-5 flex justify-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlideIndex(i)}
                  className={`w-10 h-1 bg-black ${
                    i === currentSlideIndex ? 'bg-opacity-60' : 'bg-opacity-20'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
